/**
 * AdminCompliance — Dashboard RH & Conformité (Iter386 Lot A)
 *
 * 3 onglets admin-only :
 *   1. Vue globale     → KPI, évolution mensuelle, donut statuts, tops
 *   2. Par entreprise  → tableau heures / taux / score conformité
 *   3. Par collaborateur → tableau heures cumulées / score / dernière session
 *
 * Filtre période global : 30j / 90j / 12m / Tout.
 * Design : ivoire / or / charbon — strictement aligné AdminCategoryNav.
 * Données 100% serveur (pas de hardcoding, pas de fallback inventé).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, Building2, Users, Clock, ShieldCheck,
  Loader2, ChevronLeft, ChevronRight, Award, Activity, FileArchive,
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import CampaignsTab from '../../components/admin/compliance/CampaignsTab';
import CertificatesTab from '../../components/admin/compliance/CertificatesTab';

const API = getApiUrl();

const PERIODS = [
  { id: '30d', label: '30 jours' },
  { id: '90d', label: '90 jours' },
  { id: '12m', label: '12 mois' },
  { id: 'all', label: 'Tout' },
];

const TABS = [
  { id: 'overview', label: 'Vue globale', icon: BarChart3, testid: 'compliance-tab-overview' },
  { id: 'companies', label: 'Par entreprise', icon: Building2, testid: 'compliance-tab-companies' },
  { id: 'collaborators', label: 'Par collaborateur', icon: Users, testid: 'compliance-tab-collaborators' },
  { id: 'campaigns', label: 'Campagnes & Audit', icon: FileArchive, testid: 'compliance-tab-campaigns' },
  { id: 'certificates', label: 'Certificats individuels', icon: Award, testid: 'compliance-tab-certificates' },
];

const STATUS_LABELS = {
  confirmed: 'Confirmées',
  pending_payment: 'Paiement en attente',
  pending_verification: 'À vérifier',
  report_generated: 'Brouillon rapport',
  to_pay: 'À payer',
  coach_paid: 'Coach payé',
  paid: 'Payée',
  completed: 'Terminée',
  cancelled: 'Annulée',
  canceled: 'Annulée',
  refunded: 'Remboursée',
  expired: 'Expirée',
  no_show: 'No-show',
  rejected: 'Refusée',
  validated: 'Validée',
  unknown: 'Inconnu',
};

function fmtHours(h) {
  if (h == null) return '—';
  return `${Number(h).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${Number(v).toFixed(1)} %`;
}

function scoreColor(score) {
  if (score == null) return 'text-[#6B5E48] bg-[#F3EFE3] border-[#E8E2D2]';
  if (score >= 80) return 'text-[#1F6F3A] bg-[#E8F5EC] border-[#BDE2C7]';
  if (score >= 50) return 'text-[#92710A] bg-[#FFFCEC] border-[#E8DDB5]';
  return 'text-[#9A1C1C] bg-[#FBEDED] border-[#E8C7C7]';
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const parsed = new Date(d.length <= 10 ? `${d}T00:00:00` : d);
    return parsed.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

// ─── KPI Card ───────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E8E2D2] rounded-2xl p-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#92710A]" />
        <span className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-light text-[#1F1A12] tabular-nums" style={{ fontFamily: 'Georgia, serif' }}>
        {value}
      </div>
      {hint && <div className="text-xs text-[#A89F8A] mt-1.5">{hint}</div>}
    </div>
  );
}

// ─── Tab 1 : Vue globale ────────────────────────────────────────────────
function OverviewTab({ data }) {
  if (!data) return null;
  const k = data.kpi || {};
  const monthly = data.monthly_evolution || [];
  const statuses = data.status_distribution || {};
  const totalStatuses = Object.values(statuses).reduce((a, b) => a + (b || 0), 0);
  const maxMonthlyHours = monthly.reduce((m, r) => Math.max(m, r.hours || 0), 0);

  return (
    <div className="space-y-6" data-testid="compliance-overview-content">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard
          icon={Clock}
          label="Heures certifiées"
          value={fmtHours(k.total_hours)}
          hint="Sessions verrouillées · présence vérifiée"
          testid="kpi-total-hours"
        />
        <KpiCard
          icon={ShieldCheck}
          label="Taux de conformité"
          value={fmtPct(k.conformity_rate)}
          hint={`${k.certified_sessions || 0} / ${k.total_sessions || 0} sessions`}
          testid="kpi-conformity-rate"
        />
        <KpiCard
          icon={Award}
          label="Sessions certifiées"
          value={(k.certified_sessions || 0).toLocaleString('fr-FR')}
          hint={`Sur ${(k.total_sessions || 0).toLocaleString('fr-FR')} sessions`}
          testid="kpi-certified-sessions"
        />
        <KpiCard
          icon={Building2}
          label="Entreprises actives"
          value={(k.active_companies || 0).toLocaleString('fr-FR')}
          hint="Avec au moins 1 session certifiée"
          testid="kpi-active-companies"
        />
        <KpiCard
          icon={Users}
          label="Coachs actifs"
          value={(k.active_coaches || 0).toLocaleString('fr-FR')}
          hint="Au moins 1 session certifiée"
          testid="kpi-active-coaches"
        />
        <KpiCard
          icon={Activity}
          label="Période"
          value={PERIODS.find((p) => p.id === data.period)?.label || data.period}
          hint="Plage couverte"
          testid="kpi-period"
        />
      </div>

      {/* Évolution mensuelle */}
      <div className="bg-white border border-[#E8E2D2] rounded-2xl p-6" data-testid="monthly-evolution-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold text-[#1F1A12]">Évolution mensuelle</h3>
            <p className="text-xs text-[#6B5E48]">Heures certifiées par mois (12 derniers mois)</p>
          </div>
        </div>
        {monthly.length === 0 ? (
          <div className="text-sm text-[#A89F8A] py-8 text-center">
            Aucune session certifiée sur la période.
          </div>
        ) : (
          <div className="flex items-end gap-2 h-40">
            {monthly.map((row) => {
              const h = maxMonthlyHours ? (row.hours / maxMonthlyHours) * 100 : 0;
              return (
                <div key={row.month} className="flex-1 flex flex-col items-center gap-1.5 group">
                  <div className="text-[10px] text-[#6B5E48] tabular-nums">{row.hours.toFixed(1)}</div>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-[#C49B2A] to-[#E8C46A] transition-all"
                    style={{ height: `${Math.max(h, 2)}%`, minHeight: '4px' }}
                    title={`${row.month} · ${row.hours}h · ${row.count} sessions`}
                  />
                  <div className="text-[10px] text-[#A89F8A] tabular-nums">{row.month.slice(2)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tops + statuts */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Distribution statuts */}
        <div className="bg-white border border-[#E8E2D2] rounded-2xl p-6" data-testid="status-distribution-card">
          <h3 className="text-sm font-semibold text-[#1F1A12] mb-1">Répartition des statuts</h3>
          <p className="text-xs text-[#6B5E48] mb-4">Toutes sessions (hors annulées / remboursées)</p>
          {totalStatuses === 0 ? (
            <div className="text-sm text-[#A89F8A] py-6 text-center">Aucune session sur la période.</div>
          ) : (
            <ul className="space-y-2.5">
              {Object.entries(statuses)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => {
                  const pct = totalStatuses ? (count * 100) / totalStatuses : 0;
                  return (
                    <li key={status} className="space-y-1" data-testid={`status-row-${status}`}>
                      <div className="flex items-baseline justify-between text-xs">
                        <span className="text-[#1F1A12] font-medium">
                          {STATUS_LABELS[status] || status}
                        </span>
                        <span className="text-[#6B5E48] tabular-nums">
                          {count} · {pct.toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#F3EFE3] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#C49B2A] to-[#E8C46A]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>

        {/* Top entreprises */}
        <div className="bg-white border border-[#E8E2D2] rounded-2xl p-6" data-testid="top-companies-card">
          <h3 className="text-sm font-semibold text-[#1F1A12] mb-1">Top entreprises</h3>
          <p className="text-xs text-[#6B5E48] mb-4">Par heures certifiées</p>
          {(data.top_companies || []).length === 0 ? (
            <div className="text-sm text-[#A89F8A] py-6 text-center">Aucune entreprise certifiée.</div>
          ) : (
            <ul className="divide-y divide-[#F0EADA]">
              {data.top_companies.map((c, i) => (
                <li key={c.company_id} className="py-2.5 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-[#A89F8A] tabular-nums w-5">{i + 1}.</span>
                    <span className="text-[#1F1A12] truncate font-mono text-xs">{c.company_id}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#6B5E48] tabular-nums">
                    <span>{c.sessions} sessions</span>
                    <span className="font-semibold text-[#1F1A12]">{fmtHours(c.hours)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Top coachs */}
      <div className="bg-white border border-[#E8E2D2] rounded-2xl p-6" data-testid="top-coaches-card">
        <h3 className="text-sm font-semibold text-[#1F1A12] mb-1">Top coachs</h3>
        <p className="text-xs text-[#6B5E48] mb-4">Par heures certifiées</p>
        {(data.top_coaches || []).length === 0 ? (
          <div className="text-sm text-[#A89F8A] py-6 text-center">Aucun coach certifié.</div>
        ) : (
          <ul className="divide-y divide-[#F0EADA]">
            {data.top_coaches.map((c, i) => (
              <li key={c.coach_user_id} className="py-2.5 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[#A89F8A] tabular-nums w-5">{i + 1}.</span>
                  <span className="text-[#1F1A12] truncate">{c.coach_name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#6B5E48] tabular-nums">
                  <span>{c.sessions} sessions</span>
                  <span className="font-semibold text-[#1F1A12]">{fmtHours(c.hours)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Pagination ─────────────────────────────────────────────────────────
function Pager({ page, totalPages, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-4" data-testid="pager">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="p-2 rounded-lg border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
        data-testid="pager-prev"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="text-xs text-[#6B5E48] tabular-nums">
        Page <span className="font-semibold text-[#1F1A12]">{page}</span> / {totalPages}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="p-2 rounded-lg border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
        data-testid="pager-next"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Tab 2 : Par entreprise ─────────────────────────────────────────────
function CompaniesTab({ data, onPage, onSelectCompany }) {
  if (!data) return null;
  const items = data.items || [];
  return (
    <div className="space-y-4" data-testid="compliance-companies-content">
      <div className="bg-white border border-[#E8E2D2] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F3] text-[11px] uppercase tracking-[0.12em] text-[#6B5E48]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Entreprise</th>
                <th className="text-right px-5 py-3 font-semibold">Sessions</th>
                <th className="text-right px-5 py-3 font-semibold">Certifiées</th>
                <th className="text-right px-5 py-3 font-semibold">Heures</th>
                <th className="text-right px-5 py-3 font-semibold">Taux</th>
                <th className="text-right px-5 py-3 font-semibold">Collab.</th>
                <th className="text-right px-5 py-3 font-semibold">Dernière</th>
                <th className="text-right px-5 py-3 font-semibold">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EADA]">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-sm text-[#A89F8A]">
                    Aucune entreprise sur la période.
                  </td>
                </tr>
              ) : items.map((row) => (
                <tr
                  key={row.company_id}
                  className="hover:bg-[#FBF9F3] cursor-pointer transition-colors"
                  onClick={() => onSelectCompany && onSelectCompany(row.company_id)}
                  data-testid={`company-row-${row.company_id}`}
                >
                  <td className="px-5 py-3 font-mono text-xs text-[#1F1A12]">{row.company_id}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#1F1A12]">{row.total_sessions}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#1F1A12]">{row.certified_sessions}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#1F1A12]">{fmtHours(row.hours)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#6B5E48]">{fmtPct(row.conformity_rate)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#6B5E48]">{row.active_collaborators}</td>
                  <td className="px-5 py-3 text-right text-xs text-[#6B5E48]">{fmtDate(row.last_session_date)}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={`inline-flex items-center justify-center min-w-[38px] px-2 py-1 rounded-md border text-xs font-semibold tabular-nums ${scoreColor(row.compliance_score)}`}>
                      {row.compliance_score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Pager page={data.page} totalPages={data.total_pages} onChange={onPage} />
    </div>
  );
}

// ─── Tab 3 : Par collaborateur ──────────────────────────────────────────
function CollaboratorsTab({ data, companies, companyFilter, onCompanyFilter, onPage }) {
  if (!data) return null;
  const items = data.items || [];
  return (
    <div className="space-y-4" data-testid="compliance-collaborators-content">
      {/* Filtre entreprise */}
      <div className="flex items-center gap-3">
        <label className="text-xs uppercase tracking-[0.12em] text-[#6B5E48] font-semibold">
          Filtre entreprise
        </label>
        <select
          data-testid="company-filter-select"
          value={companyFilter || ''}
          onChange={(e) => onCompanyFilter(e.target.value || null)}
          className="px-3 py-2 rounded-lg border border-[#E8E2D2] bg-white text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A]"
        >
          <option value="">Toutes les entreprises</option>
          {(companies || []).map((c) => (
            <option key={c.company_id} value={c.company_id}>{c.company_id}</option>
          ))}
        </select>
        {companyFilter && (
          <button
            type="button"
            onClick={() => onCompanyFilter(null)}
            className="text-xs text-[#92710A] hover:underline"
            data-testid="company-filter-clear"
          >
            Effacer
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E8E2D2] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F3] text-[11px] uppercase tracking-[0.12em] text-[#6B5E48]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Collaborateur</th>
                <th className="text-left px-5 py-3 font-semibold">Entreprise</th>
                <th className="text-right px-5 py-3 font-semibold">Sessions</th>
                <th className="text-right px-5 py-3 font-semibold">Certifiées</th>
                <th className="text-right px-5 py-3 font-semibold">Heures</th>
                <th className="text-right px-5 py-3 font-semibold">Dernière</th>
                <th className="text-right px-5 py-3 font-semibold">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EADA]">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-sm text-[#A89F8A]">
                    Aucun collaborateur sur la période.
                  </td>
                </tr>
              ) : items.map((row) => (
                <tr key={row.client_user_id} data-testid={`collab-row-${row.client_user_id}`}>
                  <td className="px-5 py-3 text-[#1F1A12]">
                    <div className="font-medium">{row.client_name}</div>
                    <div className="text-[11px] text-[#A89F8A] font-mono">{row.client_user_id}</div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-[#6B5E48]">
                    {row.company_id || <span className="text-[#A89F8A]">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#1F1A12]">{row.total_sessions}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#1F1A12]">{row.certified_sessions}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-[#1F1A12]">{fmtHours(row.hours)}</td>
                  <td className="px-5 py-3 text-right text-xs text-[#6B5E48]">{fmtDate(row.last_session)}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={`inline-flex items-center justify-center min-w-[38px] px-2 py-1 rounded-md border text-xs font-semibold tabular-nums ${scoreColor(row.compliance_score)}`}>
                      {row.compliance_score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.score_weights && (
        <div className="text-[11px] text-[#A89F8A] flex items-center gap-3" data-testid="score-weights-info">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>
            Score conformité = finalisation {data.score_weights.weight_finalize_ratio}% +
            présence {data.score_weights.weight_presence_ratio}% +
            durée {data.score_weights.weight_duration_ratio}%
            (configurable via Paramètres Marketplace).
          </span>
        </div>
      )}

      <Pager page={data.page} totalPages={data.total_pages} onChange={onPage} />
    </div>
  );
}

// ─── Page principale ────────────────────────────────────────────────────
export default function AdminCompliance() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('12m');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [collaborators, setCollaborators] = useState(null);
  const [companyPage, setCompanyPage] = useState(1);
  const [collabPage, setCollabPage] = useState(1);
  const [collabCompanyFilter, setCollabCompanyFilter] = useState(null);

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch overview
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/admin/compliance/overview?period=${period}`, { headers: authHeaders });
        if (!res.ok) throw new Error('Accès refusé');
        const data = await res.json();
        if (!cancel) setOverview(data);
      } catch (err) {
        if (!cancel) toast.error(err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token, period, authHeaders]);

  // Fetch by-company
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API}/admin/compliance/by-company?period=${period}&page=${companyPage}&limit=20`, { headers: authHeaders });
        if (!res.ok) throw new Error('Erreur chargement entreprises');
        const data = await res.json();
        if (!cancel) setCompanies(data);
      } catch (err) {
        if (!cancel) toast.error(err.message);
      }
    })();
    return () => { cancel = true; };
  }, [token, period, companyPage, authHeaders]);

  // Fetch by-collaborator
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const q = new URLSearchParams({ period, page: String(collabPage), limit: '20' });
        if (collabCompanyFilter) q.append('company_id', collabCompanyFilter);
        const res = await fetch(`${API}/admin/compliance/by-collaborator?${q.toString()}`, { headers: authHeaders });
        if (!res.ok) throw new Error('Erreur chargement collaborateurs');
        const data = await res.json();
        if (!cancel) setCollaborators(data);
      } catch (err) {
        if (!cancel) toast.error(err.message);
      }
    })();
    return () => { cancel = true; };
  }, [token, period, collabPage, collabCompanyFilter, authHeaders]);

  // Reset pagination on period change
  useEffect(() => {
    setCompanyPage(1);
    setCollabPage(1);
  }, [period]);

  const handleSelectCompany = (companyId) => {
    setCollabCompanyFilter(companyId);
    setCollabPage(1);
    setActiveTab('collaborators');
  };

  return (
    <div className="min-h-screen bg-[#FBF9F3]" data-testid="admin-compliance-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E8E2D2]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <Link
            to="/admin/modules"
            data-testid="admin-compliance-back-btn"
            className="inline-flex items-center gap-1.5 text-[#6B5E48] text-sm hover:text-[#1F2937] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour Dashboard
          </Link>
          <span className="text-[#A89F8A] text-xs">·</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#92710A] font-semibold">
            Administration · Conformité RH
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                data-testid={`period-${p.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  period === p.id
                    ? 'bg-[#1F1A12] text-[#F3E9C8] shadow-sm'
                    : 'text-[#6B5E48] hover:bg-[#F3EFE3]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Title */}
        <div>
          <h1 className="text-2xl font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
            Dashboard RH & Conformité
          </h1>
          <p className="text-sm text-[#6B5E48] mt-1 max-w-2xl">
            Pilotage des heures certifiées, du taux de conformité et du score
            qualité pour les sessions humaines vérifiées (présence + finalisation).
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#E8E2D2]" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(t.id)}
                data-testid={t.testid}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  active
                    ? 'border-[#C49B2A] text-[#1F1A12]'
                    : 'border-transparent text-[#6B5E48] hover:text-[#1F1A12]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading && !overview ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#92710A] animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && <OverviewTab data={overview} />}
            {activeTab === 'companies' && (
              <CompaniesTab
                data={companies}
                onPage={setCompanyPage}
                onSelectCompany={handleSelectCompany}
              />
            )}
            {activeTab === 'collaborators' && (
              <CollaboratorsTab
                data={collaborators}
                companies={companies?.items || []}
                companyFilter={collabCompanyFilter}
                onCompanyFilter={(v) => { setCollabCompanyFilter(v); setCollabPage(1); }}
                onPage={setCollabPage}
              />
            )}
            {activeTab === 'campaigns' && (
              <CampaignsTab
                api={API}
                token={token}
                companies={companies?.items || []}
              />
            )}
            {activeTab === 'certificates' && (
              <CertificatesTab api={API} token={token} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
